import { supabase } from "../supabaseClient";

const SHOWROOM_SELECT = `
  *,
  owner:profiles!showrooms_owner_id_fkey(id, name, username, avatar_url, city, state, role),
  reviews:reviews(
    id,
    showroom_id,
    user_id,
    rating,
    review,
    images,
    reply_text,
    reply_at,
    created_at,
    user:profiles!reviews_user_id_fkey(id, name, username, avatar_url, city, state)
  )
`;

export function slugifyShowroom(value = "") {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function buildShowroomSlug(name, city, area) {
  return [name, area, city].filter(Boolean).map(slugifyShowroom).filter(Boolean).join("-");
}

export function isShowroomOpen(showroom, now = new Date()) {
  if (!showroom) return false;
  const day = now.getDay();
  if (day === 0 && showroom.sunday_open === false) return false;
  if (!showroom.opening_time || !showroom.closing_time) return true;
  const current = now.getHours() * 60 + now.getMinutes();
  const [openH, openM] = String(showroom.opening_time).slice(0, 5).split(":").map(Number);
  const [closeH, closeM] = String(showroom.closing_time).slice(0, 5).split(":").map(Number);
  const open = openH * 60 + openM;
  const close = closeH * 60 + closeM;
  if (Number.isNaN(open) || Number.isNaN(close)) return true;
  if (open <= close) return current >= open && current <= close;
  return current >= open || current <= close;
}

export function formatDistanceKm(km) {
  if (km === null || km === undefined || km === "") return null;
  const value = Number(km);
  if (!Number.isFinite(value)) return null;
  return `${value.toFixed(value < 10 ? 1 : 0)} km`;
}

export async function fetchShowrooms(params = {}) {
  const {
    search = "",
    brand = "all",
    city = "all",
    state = "all",
    rating = "all",
    verified = false,
    premium = false,
    openNow = false,
    services = [],
    limit = 12,
    offset = 0,
  } = params;

  let query = supabase.from("showrooms").select(SHOWROOM_SELECT, { count: "exact" });

  if (search.trim()) {
    const term = search.trim();
    query = query.or(
      `showroom_name.ilike.%${term}%,area.ilike.%${term}%,city.ilike.%${term}%,brand.ilike.%${term}%`
    );
  }
  if (brand !== "all") query = query.ilike("brand", brand);
  if (city !== "all") query = query.ilike("city", city);
  if (state !== "all") query = query.ilike("state", state);
  if (verified) query = query.eq("verified", true);
  if (premium) query = query.eq("premium", true);
  if (services.length) query = query.overlaps("services", services);
  if (rating !== "all") query = query.gte("average_rating", Number(rating));
  if (openNow) {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const day = now.getDay();
    query = query.or(
      day === 0
        ? `sunday_open.eq.true,and(opening_time.lte.${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")},closing_time.gte.${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")})`
        : `or(opening_time.lte.${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")},closing_time.gte.${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")})`
    );
  }

  const { data, error, count } = await query
    .order("premium", { ascending: false })
    .order("verified", { ascending: false })
    .order("average_rating", { ascending: false })
    .range(offset, offset + limit - 1);

  return { data: data || [], error, count: count || 0 };
}

export async function fetchShowroomBySlug(slug) {
  return supabase.from("showrooms").select(SHOWROOM_SELECT).eq("slug", slug).single();
}

export async function fetchNearbyShowrooms({ latitude, longitude, limit = 6 }) {
  const { data, error } = await supabase
    .rpc("get_nearby_showrooms", { p_latitude: latitude, p_longitude: longitude, p_limit: limit });
  return { data: data || [], error };
}

export async function upsertShowroom(showroom) {
  const payload = {
    ...showroom,
    slug: showroom.slug || buildShowroomSlug(showroom.showroom_name, showroom.city, showroom.area),
  };
  return supabase.from("showrooms").upsert(payload, { onConflict: "slug" }).select().single();
}

export async function addShowroomReview(review) {
  return supabase.from("reviews").insert(review).select().single();
}

export async function replyToReview(reviewId, reply_text) {
  return supabase.from("reviews").update({ reply_text, reply_at: new Date().toISOString() }).eq("id", reviewId);
}

export async function toggleFavorite(showroom_id, user_id, exists) {
  if (exists) return supabase.from("favorites").delete().match({ showroom_id, user_id });
  return supabase.from("favorites").insert({ showroom_id, user_id });
}

